import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getPortalGetPortalMerchantsQueryKey,
  usePortalPostPortalMerchants,
} from '@/lib/api/portal/portal';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Schema = z.object({
  merchantId: z.string().min(1, 'Merchant id is required.').max(64),
  name: z.string().min(1, 'Name is required.').max(200),
  balanceAccountId: z.string().max(64).optional().or(z.literal('')),
});
type Form = z.infer<typeof Schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PortalCreateMerchantDialog({ open, onOpenChange }: Props) {
  const queryClient = useQueryClient();

  const mutation = usePortalPostPortalMerchants({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getPortalGetPortalMerchantsQueryKey() });
        toast.success('Merchant created.');
        onOpenChange(false);
        reset();
      },
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<Form>({
    resolver: zodResolver(Schema),
    defaultValues: { merchantId: '', name: '', balanceAccountId: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync({
        data: {
          merchantId: values.merchantId,
          name: values.name,
          balanceAccountId: values.balanceAccountId || null,
        },
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      toast.error(status === 409 ? 'A merchant with that id already exists.' : 'Could not create merchant.');
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onOpenChange(false))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New merchant</DialogTitle>
          <DialogDescription>
            An Adyen merchant account under your client. Stores sit below each merchant.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="merchantId">Merchant ID</Label>
            <Input id="merchantId" placeholder="DealerWorksPOS" autoComplete="off" {...register('merchantId')} />
            {errors.merchantId && <p className="text-xs text-destructive">{errors.merchantId.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" placeholder="DealerWorks Sales" {...register('name')} />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="balanceAccountId">Balance Account ID (optional)</Label>
            <Input id="balanceAccountId" placeholder="BA32..." autoComplete="off" {...register('balanceAccountId')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create merchant'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
